import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Scales, Check, X, CaretDown, Info, ShieldWarning, ArrowRight, Sparkle, ArrowsClockwise, CurrencyDollar } from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return "—";
  }
};

const CURATION_TONES = {
  light: { label: "Open to join · light review", bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46" },
  standards: { label: "Quality standards · no lengthy curation", bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF" },
  vetted: { label: "Application & vetting required", bg: "#FEF3C7", border: "#FDE68A", text: "#92400E" },
};

const PLATFORMS = [
  {
    key: "chairish",
    name: "Chairish",
    curation: "vetted",
    curationLabel: "Vetted · submit ~10 items to join",
    tag: "Top-tier · top-dollar vintage & design",
    best: "A top-dollar home for quality vintage-to-luxury — the same discerning buyers (designers, collectors, enthusiasts) who shop 1stDibs.",
    pros: [
      "Reaches high-intent, design-literate buyers who pay for the right piece.",
      "Integrated White-Glove + Drop-off & Go shipping handles tricky furniture logistics.",
      "Curators polish every listing (white-background photos, SEO) before it goes live.",
      "Commission eases as you grow (roughly 40% → 30% → ~20% at higher tiers).",
    ],
    cons: [
      "Vetted to join — you submit ~10 items for curatorial review before you're live.",
      "A paid seller plan (≈$49/mo) plus commission; longtime sellers were grandfathered onto earlier terms.",
      "Short buyer return window — if an item is judged “not as described,” the cost & burden of proof can land on you.",
      "Payout lands ~6 days after a sale closes & delivers (plus bank clearing).",
    ],
  },
  {
    key: "1stdibs",
    name: "1stDibs",
    curation: "vetted",
    tag: "Prestige · the highest price ceiling",
    best: "The top-dollar ceiling for rare, designer & high-value pieces — the hardest room to get into, and worth it for the right piece.",
    pros: [
      "Highest-intent luxury buyers and the strongest price ceiling for special pieces.",
      "Platform manages logistics and reduces fraud/chargeback exposure.",
      "Lower commission (~22% on vintage) is possible on a higher monthly plan.",
      "Brand trust & a Money-Back Guarantee handled by the platform.",
    ],
    cons: [
      "The hardest gate — professional/dealer status, references & a vetted portfolio; many applicants are turned down.",
      "Free-tier commission is steep (~45%); lower rates require a paid plan.",
      "Higher plans add a monthly subscription (up to ~$600+/mo) on top of commission.",
      "Mandatory 14-day returns for EU/UK buyers & Trade members even if you accept none; return shipping is usually deducted from the refund.",
    ],
  },
  {
    key: "aptdeco",
    name: "AptDeco",
    curation: "standards",
    tag: "Full-service · fast, mid-market resale",
    best: "Best for quality furniture you want moved quickly — no lengthy application ($150–$3k).",
    pros: [
      "No lengthy curation or application — meet the quality standards and you’re listing.",
      "Full-service: white-glove pickup, disassembly & delivery is arranged for you.",
      "Free to list — the fee is only taken after the item actually sells.",
      "Competitive, active buyer demand and a rich source of real-time pricing signal.",
    ],
    cons: [
      "Commission runs ~19–38% of the sale price depending on the item.",
      "Furniture only — no mattresses or electronics.",
      "Photos & descriptions must meet quality standards (5–7 staged photos + condition detail).",
      "Strongest in the metro areas served by their delivery network.",
    ],
  },
  {
    key: "rubylane",
    name: "Ruby Lane",
    curation: "vetted",
    curationLabel: "Application & shop vetting required",
    tag: "Curated antiques · knowledgeable collectors",
    best: "Best for genuine antiques & quality vintage aimed at serious, discerning collectors.",
    pros: [
      "Highly curated, collector-grade audience that values authenticity & provenance.",
      "Tiered commission is gentle on high-ticket pieces (9.9% → 5% → 2.5%).",
      "Unlimited listings for one flat $45/month.",
      "Great home for antiques/fine vintage that gets buried on mass marketplaces.",
    ],
    cons: [
      "Application required: existing online presence, business verification & item review.",
      "$45/month whether or not anything sells (the old listing rebate is gone).",
      "PayPal processing (~2.9% + $0.30) on top of commission.",
      "You arrange all shipping/freight; buyers expect to haggle (price in a ~10–15% margin).",
    ],
  },
  {
    key: "kaiyo",
    name: "Kaiyo",
    curation: "standards",
    curationLabel: "Quality standards · no application (full-service)",
    tag: "Full-service · greener, mid-market resale",
    best: "Best for quality (often mid-market) pieces you'd rather hand off entirely — they do everything.",
    pros: [
      "Totally hands-off: free pickup, cleaning, photography, storage, listing & delivery.",
      "No application — submit, and if accepted they handle the rest.",
      "'Instant Offer' option to cash out upfront at pickup.",
      "Sustainability angle appeals to eco-minded buyers.",
    ],
    cons: [
      "Big cut: you keep only ~10–60% of the sale, and Kaiyo sets the price — not you.",
      "Skews mid-market — not a luxury/gallery-grade audience.",
      "Strongest only in the metro areas their logistics cover.",
      "You give up pricing control to their algorithm.",
    ],
  },
  {
    key: "pamono",
    name: "Pamono",
    curation: "vetted",
    curationLabel: "Pro dealers only · curator-approved (EU)",
    tag: "European design buyers · high-end",
    best: "Best for design-forward vintage aimed at European collectors (now part of Chairish).",
    pros: [
      "Design-focused EU audience for mid-to-high-end vintage & contemporary design.",
      "Sliding commission rewards high-ticket pieces (20% → 15% → 10%).",
      "Now owned by Chairish — a path toward list-once, reach US + EU.",
      "Curated, authenticity-first catalog builds strong buyer trust.",
    ],
    cons: [
      "Professional dealers only — references, a real website & vetting required.",
      "Every item is curator-approved before it goes live.",
      "You arrange shipping/freight (EU logistics) yourself.",
      "Optional monthly plans (€59–€179) can add up on top of commission.",
    ],
  },
];

const SHARED_DNA = [
  "Chairish & 1stDibs are where valuable pieces fetch top dollar consistently — the same discerning buyers shop both religiously.",
  "Each is a curated, quality-first marketplace — not a fast-flip bazaar.",
  "All can manage white-glove furniture logistics so you don’t arrange freight alone.",
  "All earn through commission (and increasingly a monthly plan too) — bake that cost into your list price so your take-home is what you actually want.",
  "All reward the same things: accurate dimensions, materials, period, condition & strong photos.",
];

const CARRIES_OVER = [
  "Title / headline", "Price", "Dimensions (W / D / H)", "Materials",
  "Period / era", "Styles", "Condition", "Country of origin",
  "Photos (re-orderable, white-background option)", "Your description — re-voiced for the destination",
];

const NEEDS_ATTENTION = [
  "Getting in: 1stDibs is the hardest gate (dealer status + references + a vetted portfolio); Chairish also vets — you submit ~10 items for curatorial review; AptDeco just asks that you meet quality standards. The Application Pack is built for the curated cases.",
  "Photos: 1stDibs expects clean, professional-grade images — use the White-BG tool on your best shots.",
  "Provenance & specifics: luxury buyers on 1stDibs expect more history/detail than the Chairish export surfaces.",
  "Cost of selling: most now blend a monthly seller plan with commission — bake those fees into your list price so your take-home holds.",
  "Returns, audience & price ceiling differ — see the side-by-side below.",
];

const QA = [
  {
    q: "Who actually pays return shipping if a buyer changes their mind?",
    a: "On Chairish, a non-trade buyer usually pays — but if they claim the item was “not as described,” that cost can shift to you unless you can prove your listing was accurate. On 1stDibs, return shipping is generally deducted from the refund and routed through their logistics. The lesson either way: document condition meticulously before pickup.",
  },
  {
    q: "How fast do I really get paid?",
    a: "Chairish: roughly 6 days after the sale closes and delivers, plus a few days for bank clearing. 1stDibs: typically after the return window closes and the transaction is confirmed legitimate. Plan your cash flow around “after delivery,” not “after sale.”",
  },
  {
    q: "Why is my commission so high — and how do I protect my take-home?",
    a: "Both are luxury-retail-tier. Chairish’s free tier runs ~40%; paid seller plans (around $49/mo and up) buy lower rates (~30% → ~20% at higher tiers). 1stDibs is ~45% on its free tier, dropping to ~22% on vintage when you take a higher monthly plan. Neither is “cheap” — so the move is to bake the fee into your list price. Price for the take-home you actually want, not the sticker.",
  },
  {
    q: "Can I just sign up for 1stDibs?",
    a: "No — it’s the hardest gate of the group. You apply as a professional dealer with references and a vetted portfolio, and many applicants are turned down. There’s no fixed piece minimum to apply, but the review is rigorous. Treat it as an application, not a signup. (The Application Pack tool here is built to help you assemble it.)",
  },
  {
    q: "So where do my most valuable pieces belong?",
    a: "Chairish and 1stDibs both — that’s where valuable pieces fetch top dollar consistently, and the highest-end dealers list on both religiously. 1stDibs carries the highest ceiling and the hardest gate; Chairish reaches the same discerning buyers with a more accessible (but still vetted) door. Don’t bet one piece’s fate on a single audience — keep your record portable and list where the piece fits.",
  },
  {
    q: "What happens if an item is damaged in transit or goes missing?",
    a: "Both platforms’ guarantees are designed around the buyer’s experience. While a dispute drags on, sellers can end up absorbing the loss and even chargebacks. Keep dated, detailed condition photos of every piece before it leaves your hands — your record is your best protection.",
  },
  {
    q: "Are returns really my problem even if I list “no returns”?",
    a: "On 1stDibs, EU/UK buyers and verified Trade members are entitled to a 14-day return window regardless of your stated policy. Build that possibility into your pricing rather than assuming a sale is final.",
  },
];

const COMMON_FEEDBACK = [
  {
    theme: "Increased documentation & detail requirements",
    help: "Auto-Fill pulls dimensions, materials, period and condition from your existing listing, so you’re not re-entering everything by hand.",
    cta: "Open the workspace",
    action: "workspace",
  },
  {
    theme: "Longer listing-preparation times",
    help: "Your details, photos and descriptions are saved once and reused — with suggestions drawn from your past listings, each one gets faster than the last.",
    cta: "Open the workspace",
    action: "workspace",
  },
  {
    theme: "Shipping & coordination challenges",
    help: "Keeping one accurate, portable record means that if a sale falls through you can move the piece to another marketplace in a click — instead of starting over.",
    cta: null,
    action: null,
  },
  {
    theme: "Authentication & provenance questions",
    help: "Capture brand, designer, period and provenance once, then reuse it everywhere you list — and let AI draft the description in your own voice.",
    cta: "Open the workspace",
    action: "workspace",
  },
  {
    theme: "Fee transparency",
    help: "We keep an up-to-date, side-by-side view of each platform’s commission and fees, refreshed from their published terms.",
    cta: "See the fee chart",
    action: "fees",
  },
];

function QAItem({ item, open, onToggle, idx }) {
  return (
    <div className="border border-[#E5E5E5] rounded-lg overflow-hidden bg-white" data-testid={`guide-qa-${idx}`}>
      <button
        onClick={onToggle}
        data-testid={`guide-qa-toggle-${idx}`}
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#FDFBF7] transition-colors"
      >
        <span className="font-semibold text-base text-[#0D1B2A]">{item.q}</span>
        <CaretDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="px-4 pb-4 text-base text-[#525252] leading-relaxed" data-testid={`guide-qa-answer-${idx}`}>
          {item.a}
        </p>
      )}
    </div>
  );
}

export function MarketplaceGuideModal({ open, onClose, onExplore }) {
  const [openQA, setOpenQA] = useState(0);
  const [data, setData] = useState({ platforms: [], interval_days: 30 });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const feesRef = useRef(null);

  const handleExplore = (action) => {
    if (action === "fees") {
      feesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (action === "workspace") {
      onClose();
      onExplore?.("workspace");
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/policy-guide")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load the latest platform terms"))
      .finally(() => setLoading(false));
  }, [open]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/policy-guide/refresh");
      setData(data);
      const changed = data.platforms.filter((p) => p.updated_recently);
      if (changed.length) toast.success(`Updated — ${changed.map((p) => p.name).join(" & ")} policy changed since last check`);
      else toast.success("Checked — all platform terms are current");
    } catch (e) {
      toast.error("Couldn't refresh from sources right now");
    } finally {
      setRefreshing(false);
    }
  };

  const platforms = data.platforms || [];
  const lastChecked = platforms.map((p) => p.last_checked).filter(Boolean).sort().slice(-1)[0];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="marketplace-guide-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-[#0D1B2A] mb-1">
            <Scales size={20} weight="fill" />
            <span className="label-caps">Know before you list</span>
          </div>
          <DialogTitle className="font-serif-display text-3xl font-bold tracking-tight">
            Where to sell — what each marketplace's fine print really means
          </DialogTitle>
          <DialogDescription className="text-[#525252] text-base leading-relaxed">
            New to selling vintage or a seasoned dealer, the idea is the same: get each piece to the
            marketplace that fits it best. Here’s the honest trade-off on each — how hard they are to
            join, what they charge, and the small-print questions worth answering before your first sale.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[64vh] overflow-y-auto pr-1 space-y-6" data-testid="marketplace-guide-body">
          {/* How hard is it to get in? — curation at a glance (great for newcomers) */}
          <div className="border border-[#E5E5E5] rounded-xl bg-white p-5" data-testid="guide-curation-strip">
            <h3 className="font-serif-display text-2xl font-bold mb-1">How hard is it to get in?</h3>
            <p className="text-base text-[#525252] leading-relaxed mb-4">
              Not sure where a piece belongs? Start here. Each marketplace curates differently — this is
              the fastest way to route a piece to the home that fits it.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {PLATFORMS.map((p) => {
                const t = CURATION_TONES[p.curation];
                return (
                  <div key={p.key} className="rounded-lg border border-[#EDE7DA] bg-[#FDFBF7] p-4" data-testid={`guide-curation-${p.key}`}>
                    <p className="font-serif-display text-xl font-bold leading-none mb-2">{p.name}</p>
                    <span
                      className="inline-block text-xs font-bold px-2.5 py-1 rounded-full border"
                      style={{ backgroundColor: t.bg, borderColor: t.border, color: t.text }}
                    >
                      {p.curationLabel || t.label}
                    </span>
                    <p className="text-sm text-[#525252] leading-snug mt-2">{p.best}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Source -> destination */}
          <div className="border border-[#E5E5E5] rounded-xl bg-white p-5" data-testid="guide-source-destination">
            <h3 className="font-serif-display text-2xl font-bold mb-1">Where your data comes from — and where it’s going</h3>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#0D1B2A] bg-white px-3 py-1 text-sm font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Chairish <span className="opacity-60">· source</span>
              </span>
              <ArrowRight size={16} weight="bold" className="text-[#737373]" />
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#0D1B2A] bg-white px-3 py-1 text-sm font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> 1stDibs <span className="opacity-60">· destination</span>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#0D1B2A] bg-white px-3 py-1 text-sm font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> AptDeco <span className="opacity-60">· destination</span>
              </span>
            </div>
            <p className="text-base text-[#525252] leading-relaxed mb-4">
              Vintage Vantage reads your listings from <strong>Chairish</strong> — your source of truth today — and
              translates that one record into each <strong>destination</strong>. Because these platforms share so much
              DNA, most of the work you’ve already done carries over. Here’s exactly how they line up.
            </p>

            <div className="rounded-lg bg-[#FDFBF7] border border-[#EDE7DA] p-4 mb-4">
              <p className="text-sm font-bold uppercase tracking-wide text-[#0D1B2A] mb-2">Shared DNA</p>
              <ul className="space-y-1.5">
                {SHARED_DNA.map((x, i) => (
                  <li key={i} className="flex items-start gap-2 text-base text-[#0D1B2A] leading-snug">
                    <Check size={18} weight="bold" className="text-[#047857] shrink-0 mt-0.5" /> <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-[#047857] mb-2 inline-flex items-center gap-1.5">
                  <Sparkle size={15} weight="fill" /> Carries over automatically
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="guide-carries-over">
                  {CARRIES_OVER.map((x) => (
                    <span key={x} className="text-sm px-2.5 py-1 rounded-full border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46]">{x}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-[#B45309] mb-2">Needs your attention</p>
                <ul className="space-y-2" data-testid="guide-needs-attention">
                  {NEEDS_ATTENTION.map((x, i) => (
                    <li key={i} className="flex items-start gap-2 text-base text-[#0D1B2A] leading-snug">
                      <span className="text-[#B45309] font-bold shrink-0">→</span> <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Commission & current terms — live, refreshable */}
          <div data-testid="guide-commission-section" ref={feesRef}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <CurrencyDollar size={18} weight="fill" className="text-[#0D1B2A]" />
                <h3 className="font-serif-display text-2xl font-bold">Commission &amp; fees, side by side</h3>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#737373]" data-testid="guide-last-checked">
                  Auto-checked {fmtDate(lastChecked)} · monthly
                </span>
                <button
                  onClick={refresh}
                  disabled={refreshing}
                  data-testid="guide-refresh-button"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold border border-[#0D1B2A] text-[#0D1B2A] rounded-md px-3 py-1.5 hover:bg-[#0D1B2A] hover:text-white transition-colors disabled:opacity-50"
                >
                  <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} />
                  {refreshing ? "Checking sources…" : "Refresh from sources"}
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {platforms.map((p) => (
                <div key={p.platform_key} className="border border-[#E5E5E5] rounded-xl bg-white overflow-hidden" data-testid={`guide-fees-${p.platform_key}`}>
                  <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#F0F0F0] bg-[#FDFBF7]">
                    <div>
                      <p className="font-serif-display text-xl font-bold leading-none">{p.name}</p>
                      <p className="text-sm text-[#737373] mt-1">{p.model_label}</p>
                      {p.curation_label && (
                        <span className="inline-block text-xs font-semibold text-[#525252] mt-1.5" data-testid={`guide-fees-curation-${p.platform_key}`}>
                          {p.curation_label}
                        </span>
                      )}
                    </div>
                    {p.updated_recently && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] px-2 py-1 rounded-full" data-testid={`guide-updated-badge-${p.platform_key}`}>
                        <Sparkle size={12} weight="fill" /> Updated recently
                      </span>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-[#F0F0F0]">
                        <th className="px-4 py-2 label-caps font-semibold">Plan</th>
                        <th className="px-4 py-2 label-caps font-semibold">Commission</th>
                        <th className="px-4 py-2 label-caps font-semibold">Monthly</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(p.commission_tiers || []).map((t, i) => (
                        <tr key={i} className="border-b border-[#F7F7F7] align-top">
                          <td className="px-4 py-2 font-medium">{t.plan}</td>
                          <td className="px-4 py-2 font-bold text-[#0D1B2A]">{t.rate || "—"}</td>
                          <td className="px-4 py-2 text-[#525252]">{t.monthly || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-3 space-y-1.5 text-sm border-t border-[#F0F0F0]">
                    {p.shipping && <p><span className="font-semibold">Shipping:</span> <span className="text-[#525252]">{p.shipping}</span></p>}
                    {p.returns && <p><span className="font-semibold">Returns:</span> <span className="text-[#525252]">{p.returns}</span></p>}
                    {p.payouts && <p><span className="font-semibold">Payouts:</span> <span className="text-[#525252]">{p.payouts}</span></p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pros & cons */}
          <div className="grid md:grid-cols-2 gap-4">
            {PLATFORMS.map((p) => (
              <div key={p.key} className="border border-[#E5E5E5] rounded-xl bg-white p-5" data-testid={`guide-card-${p.key}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <h3 className="font-serif-display text-2xl font-bold">{p.name}</h3>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <p className="label-caps">{p.tag}</p>
                  {CURATION_TONES[p.curation] && (
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full border"
                      style={{
                        backgroundColor: CURATION_TONES[p.curation].bg,
                        borderColor: CURATION_TONES[p.curation].border,
                        color: CURATION_TONES[p.curation].text,
                      }}
                    >
                      {p.curationLabel || CURATION_TONES[p.curation].label}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#525252] mb-4 leading-relaxed">{p.best}</p>

                <p className="text-sm font-bold uppercase tracking-wide text-[#047857] mb-2">The upside</p>
                <ul className="space-y-2 mb-4">
                  {p.pros.map((x, i) => (
                    <li key={i} className="flex items-start gap-2 text-base text-[#0D1B2A] leading-snug">
                      <Check size={18} weight="bold" className="text-[#047857] shrink-0 mt-0.5" /> <span>{x}</span>
                    </li>
                  ))}
                </ul>

                <p className="text-sm font-bold uppercase tracking-wide text-[#B45309] mb-2">What sellers commonly flag</p>
                <ul className="space-y-2">
                  {p.cons.map((x, i) => (
                    <li key={i} className="flex items-start gap-2 text-base text-[#0D1B2A] leading-snug">
                      <X size={18} weight="bold" className="text-[#B45309] shrink-0 mt-0.5" /> <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Common seller feedback -> how VV helps */}
          <div className="rounded-xl border border-[#EDE7DA] bg-[#FDFBF7] p-5" data-testid="guide-common-feedback">
            <h3 className="font-serif-display text-2xl font-bold mb-1">Common seller feedback — and where we help</h3>
            <p className="text-base text-[#525252] leading-relaxed mb-4">
              We’re not here to criticize any marketplace. Across community discussions, sellers most
              often bring up the themes below — here’s where Vintage Vantage takes some of that work off your plate.
            </p>
            <div className="space-y-2.5">
              {COMMON_FEEDBACK.map((f, i) => (
                <div key={i} className="rounded-lg border border-[#E5E5E5] bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`guide-feedback-${i}`}>
                  <div className="flex-1">
                    <p className="font-semibold text-base text-[#0D1B2A]">{f.theme}</p>
                    <p className="text-sm text-[#525252] leading-relaxed mt-0.5">{f.help}</p>
                  </div>
                  {f.cta && (
                    <button
                      onClick={() => handleExplore(f.action)}
                      data-testid={`guide-feedback-cta-${i}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold border border-[#0D1B2A] text-[#0D1B2A] rounded-md px-3 py-2 hover:bg-[#0D1B2A] hover:text-white transition-colors"
                    >
                      {f.cta} <ArrowRight size={14} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Q&A */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Info size={18} weight="fill" className="text-[#0D1B2A]" />
              <h3 className="font-serif-display text-2xl font-bold">What you may not read until it’s too late</h3>
            </div>
            <div className="space-y-2" data-testid="marketplace-guide-qa">
              {QA.map((item, i) => (
                <QAItem key={i} idx={i} item={item} open={openQA === i} onToggle={() => setOpenQA(openQA === i ? -1 : i)} />
              ))}
            </div>
          </div>

          {/* Mission close + disclaimer */}
          <div className="rounded-xl bg-[#0D1B2A] text-[#FDFBF7] p-5" data-testid="guide-mission-note">
            <p className="font-serif-display text-xl font-semibold mb-1">The takeaway isn’t “avoid these platforms.”</p>
            <p className="text-base leading-relaxed text-[#E5E5E5]">
              It’s to go in clear-eyed and never let one marketplace own you. Keep your record portable,
              cast a wider net, and the cost of walking away from a bad policy drops to almost nothing —
              which is exactly the leverage Vintage Vantage gives back to you.
            </p>
          </div>

          <div className="flex items-start gap-2 text-sm text-[#737373]" data-testid="guide-disclaimer">
            <ShieldWarning size={16} className="shrink-0 mt-0.5" />
            <span>These reflect current policies as we understand them — and they change often, varying by plan, category and region. We work hard to keep this up to date so you can make the best decision for you, but always verify the current terms on each platform before you list. Last reviewed June 2026.</span>
          </div>
        </div>

        <div className="flex justify-end mt-2">
          <button onClick={onClose} data-testid="guide-close-button" className="bg-[#0D1B2A] text-white px-5 py-2.5 rounded-md hover:bg-black transition-colors font-medium">
            Got it
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
